/**
 * usePeopleRenderers.ts
 *
 * Registers CopilotKit v2 tool renderers for all OneThing people/team/PPP tools.
 * Call inside CopilotToolRenderers (inside <CopilotKitProvider>). Returns nothing.
 */

import { useRenderTool } from "@copilotkit/react-core/v2";
import { createElement } from "react";
import { PeopleListCard, type PersonRow } from "../cards/PeopleListCard";
import { UserProfileCard, type UserProfileData } from "../cards/UserProfileCard";
import { TeamListCard, type TeamRow } from "../cards/TeamListCard";
import { PppListCard, type PppRow } from "../cards/PppListCard";
import {
  type ToolRenderProps,
  LoadingCard,
  ErrorCard,
  RawCard,
  resolveEnvelope,
} from "./renderHelpers";

// ---------------------------------------------------------------------------
// Render helpers
// ---------------------------------------------------------------------------

function renderPeopleList(props: ToolRenderProps) {
  if (props.status === "inProgress" || props.status === "executing") {
    return createElement(LoadingCard, { label: "Loading people…" });
  }
  const env = resolveEnvelope(props);
  if (env && env.ok && env.kind === "people_list") {
    return createElement(PeopleListCard, {
      summary: env.summary,
      rows: Array.isArray(env.data) ? (env.data as PersonRow[]) : [],
    });
  }
  if (env && !env.ok) {
    return createElement(ErrorCard, { summary: env.summary, error: env.error });
  }
  return createElement(RawCard, { result: props.result });
}

function renderUserProfile(props: ToolRenderProps) {
  if (props.status === "inProgress" || props.status === "executing") {
    return createElement(LoadingCard, { label: "Loading profile…" });
  }
  const env = resolveEnvelope(props);
  if (env && env.ok && env.kind === "user_profile") {
    return createElement(UserProfileCard, { data: env.data as UserProfileData });
  }
  if (env && !env.ok) {
    return createElement(ErrorCard, { summary: env.summary, error: env.error });
  }
  return createElement(RawCard, { result: props.result });
}

function renderTeamList(props: ToolRenderProps) {
  if (props.status === "inProgress" || props.status === "executing") {
    return createElement(LoadingCard, { label: "Loading teams…" });
  }
  const env = resolveEnvelope(props);
  if (env && env.ok && env.kind === "team_list") {
    return createElement(TeamListCard, {
      summary: env.summary,
      rows: Array.isArray(env.data) ? (env.data as TeamRow[]) : [],
    });
  }
  if (env && !env.ok) {
    return createElement(ErrorCard, { summary: env.summary, error: env.error });
  }
  return createElement(RawCard, { result: props.result });
}

function renderPppList(props: ToolRenderProps) {
  if (props.status === "inProgress" || props.status === "executing") {
    return createElement(LoadingCard, { label: "Loading PPPs…" });
  }
  const env = resolveEnvelope(props);
  if (env && env.ok && env.kind === "ppp_list") {
    return createElement(PppListCard, {
      summary: env.summary,
      rows: Array.isArray(env.data) ? (env.data as PppRow[]) : [],
    });
  }
  if (env && !env.ok) {
    return createElement(ErrorCard, { summary: env.summary, error: env.error });
  }
  return createElement(RawCard, { result: props.result });
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function usePeopleRenderers(): void {
  // T08 — list all users → people_list
  useRenderTool({ name: "onething__list_users", render: renderPeopleList }, []);
  // T09 — team members → people_list
  useRenderTool({ name: "onething__get_team_members", render: renderPeopleList }, []);
  // T10 — direct reports → people_list
  useRenderTool({ name: "onething__get_direct_reports", render: renderPeopleList }, []);
  // T11 — single user profile → user_profile
  useRenderTool({ name: "onething__get_user", render: renderUserProfile }, []);
  // T12 — list teams → team_list
  useRenderTool({ name: "onething__list_teams", render: renderTeamList }, []);
  // list PPPs → ppp_list
  useRenderTool({ name: "onething__list_ppps", render: renderPppList }, []);
}
